import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { PushQuoteService } from '@zero/shared/ui-push';
import { StoreErrorHandler, getErrorMessage } from '@zero/shared/util-global-state';
import { tryGetQuoteIsin } from '@zero/shared/util-helper';
import { EASYTRADING_MATRIX_INDEX_ISINS } from 'constants/shared-constants';
import { catchError, filter, map, of, switchMap, tap } from 'rxjs';
import { TradingToolsInfrastructure } from '../infrastructure/trading-tools.infrastructure';
import { tradingToolsActions, tradingToolsApiActions } from './trading-tools.actions';

@Injectable({ providedIn: 'root' })
export class TradingToolsEffects {
  readonly #actions$ = inject(Actions);
  readonly #infra = inject(TradingToolsInfrastructure);
  readonly #storeErrorHandler = inject(StoreErrorHandler);
  readonly #pushQuoteService = inject(PushQuoteService);

  public loadUnderlyings$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(tradingToolsActions.loadUnderlyings),
      switchMap(({ segment }) =>
        this.#infra.loadUnderlyings(segment).pipe(
          map((response) =>
            response?.instruments?.map((underlying) => ({
              ...underlying,
              isIndex: EASYTRADING_MATRIX_INDEX_ISINS.includes(underlying.isin),
            })),
          ),
          map((underlyings) => tradingToolsApiActions.loadUnderlyingsSuccess({ underlyings })),
          catchError((error) => of(tradingToolsApiActions.loadUnderlyingsFailure({ error: getErrorMessage(error) }))),
        ),
      ),
    );
  });

  public onSuccessfullLoadUnderlyings$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(tradingToolsApiActions.loadUnderlyingsSuccess),
      map(({ underlyings }) => underlyings.map((underlying) => tryGetQuoteIsin(underlying.isin))),
      switchMap((isins) => this.#infra.getUnderlyingQuotes(isins).pipe(map((historicQuotes) => ({ isins, historicQuotes })))),
      switchMap(({ isins, historicQuotes }) =>
        this.#pushQuoteService.subscribeMultiBuffered(isins).pipe(
          filter((quotes) => !!quotes && Object.keys(quotes).length !== 0),
          map((quotes) => tradingToolsActions.updateUnderlyingQuotes({ quotes, historicQuotes })),
        ),
      ),
    );
  });

  public loadEasyTradingMatrix$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(tradingToolsActions.loadEasyTradingMatrix),
      switchMap(({ isin }) =>
        this.#infra.getEasyTradingMatrix(isin).pipe(
          map((response) => tradingToolsApiActions.loadEasyTradingMatrixSuccess({ matrix: response })),
          catchError((error) => of(tradingToolsApiActions.loadEasyTradingMatrixFailure({ error: getErrorMessage(error) }))),
        ),
      ),
    );
  });

  public handleBackendError$ = createEffect(
    () => {
      return this.#actions$.pipe(
        ofType(tradingToolsApiActions.loadEasyTradingMatrixFailure, tradingToolsApiActions.loadUnderlyingsFailure),
        tap(({ error }) => this.#storeErrorHandler.handleBackendError(error)),
      );
    },
    { dispatch: false },
  );
}
